import { format, isValid, parseISO } from 'date-fns';

// Leaf module (date-fns only) so the grid column builder and the admin forms can share the data
// type without either importing the other's domain.

export const DATA_TYPE_TEXT = 'text';
export const DATA_TYPE_LIST = 'select';
export const DATA_TYPE_DATE = 'date';
export const DATA_TYPE_BOOLEAN = 'boolean';

export type DataTypeValue =
  | typeof DATA_TYPE_TEXT
  | typeof DATA_TYPE_LIST
  | typeof DATA_TYPE_DATE
  | typeof DATA_TYPE_BOOLEAN;

export const DATA_TYPE_LABELS: Record<DataTypeValue, string> = {
  [DATA_TYPE_TEXT]: 'Text',
  [DATA_TYPE_LIST]: 'List',
  [DATA_TYPE_DATE]: 'Date',
  [DATA_TYPE_BOOLEAN]: 'Yes/No'
};

const DATA_TYPE_VALUES = Object.keys(DATA_TYPE_LABELS) as DataTypeValue[];

export const isDataTypeValue = (raw: string | undefined): raw is DataTypeValue =>
  DATA_TYPE_VALUES.includes(raw as DataTypeValue);

// The api's FIELD_TYPES also holds 'number', which no portal form renders.
export const normaliseFieldType = (raw: string | undefined): DataTypeValue =>
  isDataTypeValue(raw) ? raw : DATA_TYPE_TEXT;

// The one shape the api stores, mirroring its CustomFieldValueFormat::CALENDAR_DATE. Reading wider
// turns a no-op open into a rewrite.
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

// parseISO, never `new Date`: the latter reads a date-only string as UTC midnight, which is the
// previous day everywhere behind UTC.
export const parseCustomFieldDate = (value: string | null | undefined): Date | null => {
  if (!value || !CALENDAR_DATE.test(value)) return null;
  const parsed = parseISO(value);

  return isValid(parsed) ? parsed : null;
};

// A year typed one digit at a time reads as a valid year 2, then 20.
export const MIN_CUSTOM_FIELD_DATE = new Date(1900, 0, 1);
export const MAX_CUSTOM_FIELD_DATE = new Date(2099, 11, 31);

// Nullish rather than null alone: the grid's delete-to-clear hands the column undefined.
export const isAcceptableCustomFieldDate = (date: Date | null | undefined): boolean =>
  date == null || (isValid(date) && date >= MIN_CUSTOM_FIELD_DATE && date <= MAX_CUSTOM_FIELD_DATE);

export const serializeCustomFieldDate = (date: Date | null | undefined): string =>
  date && isValid(date) ? format(date, 'yyyy-MM-dd') : '';

export const formatCustomFieldDay = (date: Date | null): string =>
  date && isValid(date) ? format(date, 'MM/dd/yyyy') : '';

export const formatCustomFieldDate = (value: string | null | undefined): string => {
  const parsed = parseCustomFieldDate(value);

  return parsed ? formatCustomFieldDay(parsed) : (value ?? '');
};

export const BOOLEAN_VALUE_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

// Falls back to the stored value: hiding one is how it gets overwritten unseen.
// A value none of the options cover gets one of its own, so no editor opens blank on it.
export const optionsIncluding = (
  options: { value: string; label: string }[],
  value: string | null | undefined
): { value: string; label: string }[] =>
  !value || options.some((option) => option.value === value) ? options : [...options, { value, label: value }];

export const booleanLabel = (value: string | null | undefined): string =>
  BOOLEAN_VALUE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '';

// Written before the api narrowed to calendar dates, or from outside the portal.
export const isUnreadableStoredDate = (value: string | null | undefined): boolean =>
  !!value && parseCustomFieldDate(value) === null;

// What an editor opens on: empty for a date the picker cannot hold, so Save means what is shown.
export const editableCustomFieldValue = (fieldType: string, value: string): string =>
  normaliseFieldType(fieldType) === DATA_TYPE_DATE && isUnreadableStoredDate(value) ? '' : value;

export const customFieldDisplayValue = (fieldType: string, value: string | null | undefined): string => {
  switch (normaliseFieldType(fieldType)) {
    case DATA_TYPE_DATE:
      return formatCustomFieldDate(value);
    case DATA_TYPE_BOOLEAN:
      return booleanLabel(value);
    default:
      return value ?? '';
  }
};
